<template>
  <page-loader>
    <tip-banner>
      <div>
        The CSV you upload requires two headings: <code>mapping</code> and <code>offer</code>. Without these, the upload
        will fail.
      </div>
      <div>Also, ensure that the slugs you enter for each offer actually exist!</div>
    </tip-banner>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-text class="pa-2 pb-0">
              <drag-and-drop-upload v-model="file" accept=".csv, application/vnd.ms-excel" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                :disabled="!isPermittedTo('create') || !file || !!rowsWithErrors.length"
                :loading="isLoading"
                color="info"
                depressed
                @click="onClickUpload"
              >
                <v-icon left>cloud_upload</v-icon>Upload</v-btn
              >
            </v-card-actions>
          </v-card>
          <v-card v-if="!!rowsWithErrors.length" class="mt-4" outlined>
            <v-card-title>There were {{ rowsWithErrors.length }} rows with errors in your CSV</v-card-title>
            <v-simple-table height="500" fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Offer</th>
                    <th class="text-left">Mapping</th>
                    <th class="text-left">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rowsWithErrors" :key="`${row.mapping}-${row.offer}`">
                    <td>{{ row.offer }}</td>
                    <td>{{ row.mapping }}</td>
                    <td>
                      <div v-for="error in row.errors" :key="`${row.mapping}-${row.offer}-${error}`">
                        {{ error }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
import { mapMutations } from 'vuex'

import DragAndDropUpload from '@/components/drag-and-drop-upload'
import PageLoader from '@/components/page-loader'
import TipBanner from '@/components/tip-banner'

import PermissionsHandler from '@/mixins/permissions-handler'

import { offers, slugMap } from '@/services/offers'

export default {
  name: 'BulkAliasUpload',
  components: {
    DragAndDropUpload,
    PageLoader,
    TipBanner,
  },
  mixins: [PermissionsHandler],
  data: () => ({
    file: null,
    parsedFile: null,
    isLoading: false,
    offers: [],
  }),
  computed: {
    headers() {
      if (!this.parsedFile) return []
      const firstRow = this.parsedFile.split('\n')[0]
      const headers = firstRow.split(',')
      return headers.map(header => header.trim())
    },
    rows() {
      if (!this.parsedFile) return []
      const copy = this.parsedFile.slice()
      const rows = copy.split('\n')
      rows.shift()
      return rows
    },
    rowsWithErrors() {
      return this.rows
        .map(row => {
          const values = row.split(',').map(str => str.trim())
          const offer = values[this.offerColumn]
          const mapping = values[this.mappingColumn]

          const offerExists = this.offers.find(({ slug }) => slug === offer)
          const mappingInvalid = /[\s-!$%^&*()+|~=`{}\[\]:";<>?,.\/]/.test(mapping)

          return {
            offer,
            mapping,
            errors: [!offerExists && "Offer doesn't exist", mappingInvalid && 'Incorrect mapping format'].filter(
              Boolean
            ),
          }
        })
        .filter(({ errors }) => errors.length)
    },
    headersCorrect() {
      return this.headers.every(header => ['mapping', 'offer'].includes(header))
    },
    offerColumn() {
      return this.headers.findIndex(header => header === 'offer')
    },
    mappingColumn() {
      return this.headers.findIndex(header => header === 'mapping')
    },
  },
  watch: {
    file(file) {
      if (!file) {
        this.parsedFile = null
        return
      }
      const reader = new FileReader()

      reader.onload = e => (this.parsedFile = e.target.result)
      reader.readAsText(file)
    },
  },
  async created() {
    await this.getOffers()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async onClickUpload() {
      this.isLoading = true

      try {
        await slugMap.bulkUpload(this.file)
        this.file = undefined
        this.$notify({
          type: 'success',
          text: 'Mappings uploaded',
        })
      } catch (error) {
        console.error(error)
        this.$notify({
          type: 'error',
          text: 'Unable to upload mappings',
          data: { error },
          duration: -1,
        })
      }

      this.isLoading = false
    },
    async getOffers() {
      this.isLoading = true

      try {
        const { data } = await offers.getAllOffers({ compact: true })
        this.offers = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve offers',
          data: { error },
          duration: -1,
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.eyeballs {
  height: 100%;
  user-select: none;
}
</style>
